<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import { useFetchUserGroups } from "~/modules/user/composables/useFetchUserGroups";
import { PATH_USER_MANAGEMENT_GROUPS_CODE } from "~/modules/user/constants";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import { router } from "~/plugins/router";
import { Filters, SectorResource } from "~/types";

const props = defineProps<{
  sector: Sector;
}>();

const { currentOrganization } = storeToRefs(useOrganizationStore());
const { t } = useI18n();
const {
  isLoading,
  results: userGroups,
  error,
  fetchData,
  canManageItems,
  getDescription,
  filters,
  setFilter,
  search,
  onSearch,
} = useFetchUserGroups({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
});

const selectedItems: Ref<GroupsUserV2[]> = ref([]);
const displayActions = computed(() => canManageItems(props.sector));

const searchQuery = computed({
  get() {
    return (filters.get(Filters.NAME) ?? "") as string;
  },
  set(searchQuery) {
    setFilter(Filters.NAME, searchQuery);
  },
});

async function fetchSectorUserGroups(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  const params = new Map();
  await fetchData(page, pageSize, params);
}

async function refresh() {
  await fetchSectorUserGroups();
  selectedItems.value = [];
}

function onRowClick(userGroup: GroupsUserV2) {
  router.push({
    name: PATH_USER_MANAGEMENT_GROUPS_CODE,
    params: { code: userGroup.name },
  });
}

onSearch(refresh);

defineExpose({
  refresh,
});
</script>

<template>
  <container-component
    :px="0"
    :py="0"
    :is-loading="isLoading && !userGroups"
    :error="error"
  >
    <search-bar
      v-model:search-query="searchQuery"
      class="mb-4"
      :search-placeholder="t('userGroup.search')"
      @clear="search"
      @search="search"
    />
    <sectors-details-table
      v-model="selectedItems"
      primary-key="name"
      :is-loading="isLoading"
      :selection="displayActions"
      :min-width="displayActions ? '23rem' : '20rem'"
      :items="userGroups"
      :display-actions="displayActions"
      :resource="SectorResource.USER_GROUP"
      :get-description="getDescription"
      :sector="sector"
      selection-message="sectors.selectedUserGroups"
      @pagination:get-next-page="fetchSectorUserGroups"
      @row-click="onRowClick"
      @actions:detach="refresh"
    >
      <lxc-table-column
        prop="name"
        :label="t('table.header.label')"
        min-width="20rem"
      />
    </sectors-details-table>
  </container-component>
</template>
